import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { dispatch } from '../store';
import {
  cancelStep5Intent,
  getStep5Status,
  triggerPerfReport,
} from '../../services/step5Api';
import {
  Step5IntentKind,
  Step5State,
  Step5StatusResponse,
} from '../../types/step5';

const initialState: Step5State = {
  status: null,
  isLoading: false,
  loadError: null,
  isTriggeringPerfReport: false,
  triggerPerfReportError: null,
  isCancellingIntent: null,
  cancelIntentError: null,
};

const step5Slice = createSlice({
  name: 'step5',
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setStatus(state, action: PayloadAction<Step5StatusResponse | undefined>) {
      state.status = action.payload;
    },
    setLoadError(state, action: PayloadAction<string | null>) {
      state.loadError = action.payload;
    },
    setTriggeringPerfReport(state, action: PayloadAction<boolean>) {
      state.isTriggeringPerfReport = action.payload;
    },
    setTriggerPerfReportError(state, action: PayloadAction<string | null>) {
      state.triggerPerfReportError = action.payload;
    },
    setCancellingIntent(state, action: PayloadAction<Step5IntentKind | null>) {
      state.isCancellingIntent = action.payload;
    },
    setCancelIntentError(state, action: PayloadAction<string | null>) {
      state.cancelIntentError = action.payload;
    },
    resetStep5State() {
      return initialState;
    },
  },
});

export const {
  setLoading,
  setStatus,
  setLoadError,
  setTriggeringPerfReport,
  setTriggerPerfReportError,
  setCancellingIntent,
  setCancelIntentError,
  resetStep5State,
} = step5Slice.actions;

/**
 * Fetch the unified Step 5 status. Pass ``silent=true`` for polling so we don't toggle the
 * page-level spinner on every tick (the mount call should be non-silent so we render a
 * loading state until the first response).
 */
export function fetchStep5Status(
  projectId: number,
  runId: number,
  options: { silent?: boolean } = {}
) {
  return async () => {
    if (!projectId || !runId) return;

    if (!options.silent) {
      dispatch(setLoading(true));
    }
    dispatch(setLoadError(null));

    try {
      const response = await getStep5Status(projectId, runId);
      dispatch(setStatus(response));
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.displayMessage ||
        err?.message ||
        'Failed to load Step 5 status.';
      dispatch(setLoadError(message));
    } finally {
      if (!options.silent) {
        dispatch(setLoading(false));
      }
    }
  };
}

/**
 * Trigger Step 5a. The backend serialises behind scaling / running replay, so the response
 * may be QUEUED rather than PENDING — the caller doesn't need to special-case either since
 * we just re-fetch status on success and let the UI re-render off the polled state.
 */
export function startPerfReport(projectId: number, runId: number) {
  return async () => {
    if (!projectId || !runId) {
      dispatch(setTriggerPerfReportError('No project run selected.'));
      return;
    }
    dispatch(setTriggeringPerfReport(true));
    dispatch(setTriggerPerfReportError(null));

    try {
      await triggerPerfReport(projectId, runId);
      // Refetch immediately so the card flips off the "Trigger" CTA without waiting for
      // the next poll tick.
      await dispatch(
        fetchStep5Status(projectId, runId, { silent: true }) as any
      );
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.displayMessage ||
        err?.message ||
        'Failed to trigger the performance report.';
      dispatch(setTriggerPerfReportError(message));
    } finally {
      dispatch(setTriggeringPerfReport(false));
    }
  };
}

/**
 * Clear a queued (or rejected) STEP5_INTENT entry. Re-fetch status on success so the UI
 * immediately reflects the cleared state.
 */
export function clearStep5Intent(
  projectId: number,
  runId: number,
  kind: Step5IntentKind
) {
  return async () => {
    if (!projectId || !runId) {
      dispatch(setCancelIntentError('No project run selected.'));
      return;
    }
    dispatch(setCancellingIntent(kind));
    dispatch(setCancelIntentError(null));

    try {
      await cancelStep5Intent(projectId, runId, kind);
      await dispatch(
        fetchStep5Status(projectId, runId, { silent: true }) as any
      );
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.displayMessage ||
        err?.message ||
        'Failed to cancel the queued request.';
      dispatch(setCancelIntentError(message));
    } finally {
      dispatch(setCancellingIntent(null));
    }
  };
}

export default step5Slice.reducer;
